import styles from "./Expander.module.css";
import PropTypes from "prop-types";
import { useFreshState } from "shared/hooks/useFreshState";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useEffect } from "react";

/**
 * Collapsible content container. Click the header to toggle collapsed state.
 * Provide your own header style through renderHeader and pass children for content.
 */
export function Expander({
  isOpen,
  renderHeader,
  toggleOnClick = true,
  onToggle,
  children,
}) {
  // Using fresh state allows for tracking local state which gets updated when 'isOpen' changes.
  const [open, setOpen] = useFreshState(isOpen || false);
  const [wasOpen, setWasOpen] = useState(isOpen || false);
  const [initialized, setInitialized] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const updateSize = useCallback(
    (animate = true) => {
      const container = containerRef.current;
      const content = contentRef.current;
      const contentHeight = content.getBoundingClientRect().height;

      if (animate) {
        const animOptions = {
          duration: animate ? 400 : 0,
          easing: "cubic-bezier(0.25, 1, 0.5, 1)",
          fill: "forwards",
        };

        let containerKeyFrames = [];
        let contentKeyFrames = [];

        if (open) {
          containerKeyFrames = [
            { height: "0px" },
            { height: `${contentHeight}px` },
          ];
          contentKeyFrames = [
            { transform: `translateY(${-contentHeight / 2}px)`, opacity: 0 },
            { transform: `translateY(0)`, opacity: 1 },
          ];
        } else {
          containerKeyFrames = [
            { height: `${contentHeight}px` },
            { height: "0px" },
          ];
          contentKeyFrames = [
            { transform: `translateY(0)`, opacity: 1 },
            { transform: `translateY(${-contentHeight / 2}px)`, opacity: 0 },
          ];
        }

        container
          .animate(containerKeyFrames, animOptions)
          .finished.then((anim) => {
            if (open) {
              // Remove opening animation when it ends to allow the container height to change when content changes.
              container.style.height = "unset";
            } else {
              container.style.height = "0px";
            }

            anim.cancel();
          });

        content.animate(contentKeyFrames, animOptions);
      } else {
        if (open) {
          container.style.height = "unset";
        } else {
          container.style.height = "0px";
        }
      }
    },
    [open]
  );

  useLayoutEffect(() => {
    // Call onToggle if open value has actually changed and isn't what's being passed in.
    if (onToggle instanceof Function && wasOpen !== open && open !== isOpen) {
      onToggle(open);
      setWasOpen(open);
    }

    // Snap to initial position.
    if (!initialized) {
      setInitialized(true);
      updateSize(false);

      return;
    }

    updateSize();
  }, [open, wasOpen]);

  return (
    <div className={styles.expander}>
      <div
        className={styles.header}
        onClick={(e) => {
          e.preventDefault();
          if (toggleOnClick) {
            setOpen(!open);
          }
        }}
      >
        {renderHeader(open, setOpen)}
      </div>

      <article ref={containerRef} className={styles.container}>
        <div ref={contentRef} className={styles.content}>
          {children}
        </div>
      </article>
    </div>
  );
}

Expander.propTypes = {
  /**
   * Pass this to control open/closed state from outside the component.
   */
  isOpen: PropTypes.bool,

  /**
   * Set this to false if you want to manage open/close states yourself inside the renderHeader function.
   * This is handled by default so you don't have to implement it every time.
   */
  toggleOnClick: PropTypes.bool,

  /**
   * Function to render the header. Receives 'open' boolean and 'setOpen' function as parameters.
   */
  renderHeader: PropTypes.func.isRequired,

  /**
   * Optional callback function to track toggle state. Called whenever the state changes and is passed the new state as a boolean.
   */
  onToggle: PropTypes.func,
};
